import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import _uniq from 'lodash/uniq';
import React from 'react';
import { FallbackImage } from '../../../Components/FallbackImage';
import { CampaignType, ProductRewardSearchResult } from '../../../types';
import {
  decodeHTMLCharacters,
  formatMoney,
  getAppConsistentParams,
  getEntityImage,
  getProductUrl,
  getPurchaseRewardAmountForProduct,
} from '../../../utils';
import { BaseComponentPropsType } from '../../type';
import { MOBILE_SCREEN_SIZE, TABLET_SCREEN_SIZE } from '../../../utils/constants';
import { RetailerIconGroup } from '../RetailerIconGroup';
import { RewardPillWithText } from '../RewardPill/RewardPillWithText';

const ProductCard = styled('div')(({ theme }) => ({
  border: '1px solid #dcdddc',
  width: '100%',
  height: '100%',
  borderRadius: 12,
  padding: '16px 16px 16px 22px',
  cursor: 'pointer',
  boxSizing: 'border-box',
  transition: 'background-color 0.4s ease-in-out',
  '&:hover': {
    backgroundColor: '#F6F8FA',
  },
  '*': {
    mixBlendMode: 'multiply',
  },

  [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: 10,
    width: '100%',
  },
  '.image': {
    width: 'calc(100% - 24px)',
    maxWidth: '190px',
    aspectRatio: '1/1',
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '16px auto 0',
    img: {
      maxHeight: '100%',
      maxWidth: '100%',
      aspectRatio: '1',
      objectFit: 'contain',
    },
  },
  '.title': {
    textDecoration: 'none',
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 500,
    marginTop: 20,
    width: '100%',
    overflow: 'hidden',
    letterSpacing: 0.36,
    WebkitLineClamp: 3,
    lineHeight: 1.5,
    height: `4.5em`, // line height * 2
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
    [`@media screen and (max-width: ${1250}px)`]: {
      fontSize: 16,
    },
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 12,
    },
  },
  '.price': {
    color: theme.palette.primary.main,
    fontSize: 20,
    fontWeight: 600,
    marginTop: 10,
    marginBottom: 10,
    display: 'flex',
    columnGap: 12,
    alignItems: 'center',

    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 16,
    },
    '.value': {
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

const RewardBarContainer = styled('div', {
  shouldForwardProp: (prop) => !['clickable'].includes(prop.toString()),
})<{ clickable?: boolean }>(({ clickable, theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: '#ecf9f6',
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
  padding: '8px 15px',
  borderRadius: 99,
  boxSizing: 'border-box',
  fontSize: 14,
  ':hover': {
    cursor: clickable ? 'pointer' : 'default',
  },
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 8px',
  },
  '.reward_star': {
    width: 12,
    height: 12,
    marginRight: 7,
  },
}));

export type RewardBarProps = {
  text: string;
  style?: any;
  onClick?: () => void;
  withDots?: boolean;
};

export const RewardBar = ({ text, style = {}, onClick, withDots }: RewardBarProps) => {
  const handleClick = () => {
    if (onClick) onClick();
  };
  return (
    <RewardBarContainer style={style} onClick={handleClick} clickable={!!onClick}>
      <img
        loading="lazy"
        className="reward_star"
        src="https://media.brandclub.com/brandclub/image_asset/star-yellow.svg"
        alt="family_bg"
      />
      {text}
      {withDots ? (
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ minWidth: 10, maxWidth: 50, width: '100%' }}></div>
          <MoreHorizIcon sx={(theme) => ({ color: theme.palette.primary.main, fontSize: 16 })} />
        </div>
      ) : null}
    </RewardBarContainer>
  );
};

export const BrandclubProductCard = ({
  product,
  LinkComponent,
  className = '',
  onShareClick,
}: { product: ProductRewardSearchResult; className?: string } & {
  LinkComponent: BaseComponentPropsType['LinkComponent'];
  onShareClick?: BaseComponentPropsType['onShareClick'];
}) => {
  const {
    affiliateRewardAmount,
    hasActiveInstantReward,
    matchedProducts,
    retailerId,
    retailPrice,
    stacklineSku,
    title,
  } = product;
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const targetUrl = getProductUrl({ title, stacklineSku, urlSearchParams });

  const img = getEntityImage(stacklineSku, 'product');
  const cleanProductTitle = decodeHTMLCharacters(title || '');

  const purchaseRewardAmount = getPurchaseRewardAmountForProduct(product);
  const rewardPercent = !!retailPrice && !!purchaseRewardAmount ? (purchaseRewardAmount * 100) / retailPrice : 0;

  const matchedProductsRetailerIds = (matchedProducts ?? []).map((matchedProduct) => matchedProduct.retailerId);
  const retailerIds = _uniq([retailerId, ...matchedProductsRetailerIds]);

  const theme = useTheme();
  const smBreakpoint = theme.breakpoints.values.sm;
  const tabletView = useMediaQuery(`(min-width: ${smBreakpoint}px)`);
  const mobileView = !tabletView;

  const retailerLogoWidth = mobileView ? 15 : 25;
  return (
    <LinkComponent to={`${targetUrl}`} style={{ textDecoration: 'none' }}>
      <ProductCard className={className + ' card-link'}>
        <RewardPillWithText
          onPillClick={onShareClick}
          rewardAmount={affiliateRewardAmount ?? 0}
          rewardCampaignType={CampaignType.AffiliateRewardCampaign}
          containerStyle={{ justifyContent: 'end' }}
          baseFontSize={mobileView ? 11 : 14}
        />
        <div className="image">
          <FallbackImage
            src={img}
            fallbackImage={img.replace('media.brandclub.com', 'media-api.brandclub.com')}
            alt={title}
          />
        </div>
        <div className="title">{cleanProductTitle}</div>
        <div className="price">
          <div className="value">{formatMoney(retailPrice)}</div>
          <RetailerIconGroup retailerIds={retailerIds} iconSize={retailerLogoWidth} maxSize={5} />
        </div>
        <RewardPillWithText
          rewardAmount={purchaseRewardAmount ?? 0}
          rewardCampaignType={
            hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign
          }
          rewardPercent={rewardPercent}
          rewardPercentText="reward"
          containerStyle={mobileView ? { columnGap: 8 } : undefined}
          baseFontSize={mobileView ? 11 : 14}
        />
      </ProductCard>
    </LinkComponent>
  );
};
